import ConfirmationDialog from '@/components/Base/modals/ConfirmationDialog'
import SaveAndLoadModal from '@/components/Base/modals/SaveLoadModal'
import CandidateScreensAccordion from '@/components/PortfolioOptimization/Accordions/CandidateScreens/CandidateScreenAccordion'
import OptimizationGoalsAccordion from '@/components/PortfolioOptimization/Accordions/OptimizationGoalAndConstraints/OptimizationGoalsAccordion'
import OptimizationParametersAccordion from '@/components/PortfolioOptimization/Accordions/OptimizationParameters/OptimizationParameterAccordion'
import SelectPortfolioAccordion from '@/components/PortfolioOptimization/Accordions/SelectPortfolios/SelectPortfoliosAccordion'
import StartNewOptimizationButton from '@/components/PortfolioOptimization/StartNewOptimizationButton'
import {
  DUPLICATE_TEMPLATE_NAME_MESSAGE,
  UNSAVED_CHANGES_MESSAGE,
} from '@/constants/portfolioOptimization.constant'
import {
  DEFAULT_CONTAINER_COL_STYLE,
  FLEX_ROW_SPACE_BETWEEN,
} from '@/constants/scenarioBuilder.constant'
import {
  AccordionKeys,
  SaveAndLoadModalAction,
} from '@/enums/portfolio-optimization.enum'
import { PortfolioOptimizationLayout } from '@/Layouts/PortfolioOptimizationLayout'

import LoadingButton from '@/components/Shared/LoadingButton'
import { DATE_FORMAT } from '@/constants/common.constant'
import { useAppSelector } from '@/hooks/use-app-selector'
import {
  IOptimizationPayload,
  IPortfolio,
  IPortfolioOptimizationTemplate,
  IRunPortfolioOptimizationTemplateRequestBody,
} from '@/interfaces/portfolioOptimization.interface'
import {
  createPortfolioOptimizationTemplate,
  getPortfolioOptimizationTemplateDetails,
  getPortfolioOptimizationTemplates,
  runPortfolioOptimizationTemplate,
  updatePortfolioOptimizationTemplate,
} from '@/services/portfolio-optimization/portfolio-optimization-template.service'
import {
  resetScreening,
  resetSubsetScreening,
  setCriteriaList,
  setScreeningCriteriasList,
} from '@/states/candidateScreen.slice'
import {
  resetConstraints,
  setConstraint,
} from '@/states/optimizationConstraint.slice'
import {
  clearAll,
  setAccordionComplete,
  updatePortfolioOptimizationFormData,
} from '@/states/portfolioOptmization.slice'
import { showSnackbar } from '@/states/snackbar.slice'
import { validateTemplateName } from '@/utils/UserInputValidation'
import { Box, Button, Paper } from '@mui/material'
import dayjs from 'dayjs'
import React, { useEffect, useState } from 'react'
import { useDispatch } from 'react-redux'
import { v4 as uuidv4 } from 'uuid'

export const PortfolioOptimizationPage: React.FC = () => {
  const isComplete = useAppSelector((s) =>
    Boolean(
      s.portfolioOptimization.accordions.SELECT_PORTFOLIOS &&
        s.portfolioOptimization.accordions.OPTIMIZATION_PARAMETER &&
        s.portfolioOptimization.accordions.CANDIDATE_SCREEN &&
        s.portfolioOptimization.accordions.OPTIMIZATION_CONSTRAINTS,
    ),
  )

  // Template List
  const [optimizationTemplateList, setOptimizationTemplateList] = useState<
    IPortfolioOptimizationTemplate[]
  >([])
  const [loadingTemplateList, setLoadingTemplateList] = useState(false)

  // Selected Template
  const [selectedOptimizationTemplate, setSelectedOptimizationTemplate] =
    useState<IPortfolioOptimizationTemplate | null>(null)

  // Run
  const [runPortfolioOptimizationLoading, setRunPortfolioOptimizationLoading] =
    useState(false)

  // Save Modal
  const [saveModalOpen, setSaveModalOpen] = useState(false)
  const [confirmModalOpen, setConfirmModalOpen] = useState(false)
  const [
    confirmDuplicateTemplateExistModalOpen,
    setConfirmDuplicateTemplateExistModalOpen,
  ] = useState(false)
  const [loading, setLoading] = useState(false)
  const [optimizationName, setOptimizationName] = useState('')
  const dispatch = useDispatch()
  const optimizationPayload = useAppSelector(
    (s) => s.portfolioOptimization.formData,
  )

  const constraints = useAppSelector(
    (s) => s?.optimizationConstraint?.template?.constraints,
  )

  const candidateScreenState = useAppSelector((s) => s?.candidateScreen)

  const handleStartNewOptimizationClick = () => {
    setConfirmModalOpen(false)
    clearAllStates()
  }

  const handleConfirmTemplateName = () => {
    setConfirmDuplicateTemplateExistModalOpen(false)
    const optimizationTemplate = optimizationTemplateList.find(
      (template) => template.longName === optimizationName,
    )
    const payload = {
      ...optimizationPayload,
      id: optimizationTemplate?.shortName,
      name: optimizationName,
      constraints,
      screenings: candidateScreenState?.screeningCriteriasList,
    }
    updateTemplate(payload, optimizationTemplate?.shortName as string)
  }

  const createNewTemplate = async (payload: IOptimizationPayload) => {
    setLoading(true)
    try {
      const res = await createPortfolioOptimizationTemplate(payload)
      dispatch(
        showSnackbar({
          message: `Optimization template ${res.result} created successfully`,
          severity: 'success',
        }),
      )
      if (res.result) {
        loadSelectedOptimizationDetails({
          shortName: res.result,
          longName: optimizationName,
        })
      }
    } catch (error) {
      dispatch(
        showSnackbar({
          message: 'Failed to create optimization template',
          severity: 'error',
        }),
      )
    } finally {
      setLoading(false)
      setSaveModalOpen(false)
      fetchOptimizationTemplateList()
    }
  }

  const updateTemplate = async (payload: IOptimizationPayload, id: string) => {
    try {
      setLoading(true)
      const res = await updatePortfolioOptimizationTemplate(id, payload)
      dispatch(
        showSnackbar({
          message: `Optimization template ${res.result} updated successfully`,
          severity: 'success',
        }),
      )
      if (res.result) {
        loadSelectedOptimizationDetails({
          shortName: res.result,
          longName: optimizationName,
        })
      }
    } catch (error) {
      dispatch(
        showSnackbar({
          message: 'Failed to update optimization template',
          severity: 'error',
        }),
      )
    } finally {
      setLoading(false)
      setSaveModalOpen(false)
      fetchOptimizationTemplateList()
    }
  }

  const handleSaveOptimization = async () => {
    const validation = validateTemplateName(optimizationName)
    if (!validation.isValid) {
      dispatch(
        showSnackbar({
          message: validation.error || 'Invalid template name',
          severity: 'error',
        }),
      )
      return
    }
    const isTemplateNameExist = optimizationTemplateList.find(
      (template) => template.longName === optimizationName,
    )
    if (isTemplateNameExist) {
      setConfirmDuplicateTemplateExistModalOpen(true)
    } else {
      const payload = {
        ...optimizationPayload,
        name: optimizationName,
        constraints,
        screenings: candidateScreenState?.screeningCriteriasList,
      }
      delete payload.id
      createNewTemplate(payload)
    }
  }

  /**
   * Loads the details of the selected optimization template
   * @param option The optimization template to load
   */
  const loadSelectedOptimizationDetails = async (
    option: IPortfolioOptimizationTemplate,
  ) => {
    setLoading(true)
    setSelectedOptimizationTemplate(option)
    setOptimizationName(option.longName)
    try {
      const details = await getPortfolioOptimizationTemplateDetails(
        option.shortName,
      )
      // Update Form Data
      dispatch(
        updatePortfolioOptimizationFormData({
          id: details.id,
          name: details.name,
          optimizationParams: details.optimizationParams,
          screenings: details.screenings,
          constraints: details.constraints,
        }),
      )
      if (details.optimizationParams?.params) {
        dispatch(
          setAccordionComplete({
            accordionType: AccordionKeys.OPTIMIZATION_PARAMETER,
            isComplete: true,
          }),
        )
      } else {
        dispatch(
          setAccordionComplete({
            accordionType: AccordionKeys.OPTIMIZATION_PARAMETER,
            isComplete: false,
          }),
        )
        dispatch(
          showSnackbar({
            message: 'No Parameters found for this template',
            severity: 'warning',
          }),
        )
      }
      // Update Constraints
      const constraintsWithRowKeys = details.constraints?.map((item) => {
        const rowKey = item.rowKey || uuidv4()
        return {
          ...item,
          rowKey,
        }
      })
      dispatch(setConstraint(constraintsWithRowKeys || []))
      if (details.constraints.length > 0) {
        dispatch(
          setAccordionComplete({
            accordionType: AccordionKeys.OPTIMIZATION_CONSTRAINTS,
            isComplete: true,
          }),
        )
      } else {
        dispatch(
          setAccordionComplete({
            accordionType: AccordionKeys.OPTIMIZATION_CONSTRAINTS,
            isComplete: false,
          }),
        )
        dispatch(
          showSnackbar({
            message: 'No constraints found for this template',
            severity: 'warning',
          }),
        )
      }

      // Update Screening
      const screeningsWithRowKeys = details.screenings?.map((s) => {
        const rowKey = s.rowKey || uuidv4()
        return {
          ...s,
          rowKey,
        }
      })

      dispatch(
        setCriteriaList(
          screeningsWithRowKeys.map((s) => {
            return {
              id: s?.data?.axiomScreenItemData?.item || '',
              screenCriteria: s?.data?.axiomScreenItemData?.title || '',
              noOfSecurities: null,
              rowKey: s.rowKey,
            }
          }),
        ),
      )

      dispatch(setScreeningCriteriasList(screeningsWithRowKeys))
      if (details.screenings.length > 0) {
        dispatch(
          setAccordionComplete({
            accordionType: AccordionKeys.CANDIDATE_SCREEN,
            isComplete: true,
          }),
        )
      } else {
        dispatch(
          setAccordionComplete({
            accordionType: AccordionKeys.CANDIDATE_SCREEN,
            isComplete: false,
          }),
        )
        dispatch(
          showSnackbar({
            message: 'No screenings found for this template',
            severity: 'warning',
          }),
        )
      }
      dispatch(
        showSnackbar({
          message: `Successfully loaded ${details?.id}: ${details?.name}`,
          severity: 'success',
        }),
      )
    } catch (error) {
      dispatch(
        showSnackbar({
          message: `Failed to load optimization template ${option.shortName}: ${option.longName}`,
          severity: 'error',
        }),
      )
    } finally {
      setLoading(false)
    }
  }

  /**
   * Fetches the list of saved optimization templates from the backend
   */
  const fetchOptimizationTemplateList = async () => {
    setLoadingTemplateList(true)
    try {
      const templates = await getPortfolioOptimizationTemplates()
      setOptimizationTemplateList(templates)
    } catch (error) {
      dispatch(
        showSnackbar({
          message: 'Failed to fetch optimization templates',
          severity: 'error',
        }),
      )
    } finally {
      setLoadingTemplateList(false)
    }
  }

  const [selectedPortfolios, setSelectedPortfolios] = useState<IPortfolio[]>([])

  const handleRunPortfolioOptimization = async () => {
    if (selectedPortfolios?.length === 0) {
      dispatch(
        showSnackbar({
          message: 'Please select at least one portfolio',
          severity: 'error',
        }),
      )
      return
    }

    if (!selectedOptimizationTemplate) {
      dispatch(
        showSnackbar({
          message: 'Please select an optimization template',
          severity: 'error',
        }),
      )
      return
    }

    selectedPortfolios?.forEach((portfolio) => {
      runPortfolioOptimization({
        portfolioName: portfolio.name,
        templateName: selectedOptimizationTemplate.shortName,
        analysisDate: dayjs(portfolio.date).format(DATE_FORMAT.API_DATE_TIME),
      })
    })
  }

  const runPortfolioOptimization = async (
    payload: IRunPortfolioOptimizationTemplateRequestBody,
  ) => {
    setRunPortfolioOptimizationLoading(true)
    try {
      const res = await runPortfolioOptimizationTemplate(payload)
      dispatch(
        showSnackbar({
          message: res.message,
          severity: 'success',
        }),
      )
    } catch (error) {
      dispatch(
        showSnackbar({
          message: 'Failed to run optimization',
          severity: 'error',
        }),
      )
    } finally {
      setRunPortfolioOptimizationLoading(false)
    }
  }

  const clearAllStates = () => {
    dispatch(clearAll())
    dispatch(resetConstraints())
    dispatch(resetScreening())
    dispatch(resetSubsetScreening())
  }

  useEffect(() => {
    clearAllStates()
    fetchOptimizationTemplateList()
  }, [])

  return (
    <PortfolioOptimizationLayout>
      <Paper sx={{ minHeight: 'calc(100vh - 68px - 16px )' }}>
        <Box sx={DEFAULT_CONTAINER_COL_STYLE}>
          <Box
            sx={{
              ...FLEX_ROW_SPACE_BETWEEN,
              borderBottom: '1px solid #E5E5E5',
              padding: 2,
            }}
          >
            <StartNewOptimizationButton
              label='Start New Optimization'
              options={optimizationTemplateList}
              onPrevOptionClick={loadSelectedOptimizationDetails}
              onStartNewClick={() => setConfirmModalOpen(true)}
              loading={loadingTemplateList}
              selectedOption={selectedOptimizationTemplate}
            />
            <Box sx={{ display: 'flex', gap: 1 }}>
              <LoadingButton
                loading={runPortfolioOptimizationLoading}
                variant='contained'
                size='medium'
                onClick={handleRunPortfolioOptimization}
                disabled={
                  selectedPortfolios?.length === 0 ||
                  !selectedOptimizationTemplate
                }
              >
                Run Portfolio Optimization
              </LoadingButton>
              <Button
                variant='outlined'
                size='medium'
                onClick={() => setSaveModalOpen(true)}
                disabled={!isComplete}
              >
                Save Optimization Workflow
              </Button>
            </Box>
          </Box>
          <Box
            className='optimization-accordion-container'
            sx={{ ...DEFAULT_CONTAINER_COL_STYLE, padding: 2, gap: 0.5 }}
          >
            <SelectPortfolioAccordion
              selectedPortfolios={selectedPortfolios}
              updateSelectedPortfolios={setSelectedPortfolios}
            />
            <OptimizationParametersAccordion />
            <CandidateScreensAccordion />
            <OptimizationGoalsAccordion />
          </Box>
        </Box>

        <SaveAndLoadModal
          isOpen={saveModalOpen}
          onClose={() => setSaveModalOpen(false)}
          mode={SaveAndLoadModalAction.SAVE}
          currentValue={optimizationName}
          options={[]}
          onChange={(name) => {
            setOptimizationName(name)
            dispatch(
              updatePortfolioOptimizationFormData({
                ...optimizationPayload,
                name: optimizationName,
              }),
            )
          }}
          onConfirm={handleSaveOptimization}
          loading={loading}
          saveTitle='Save Optimization Workflow'
          savePlaceholder='Type optimization name to save'
          saveButtonText='Save'
        />

        <ConfirmationDialog
          open={confirmModalOpen}
          title='Confirm Start New Optimization'
          contentText={UNSAVED_CHANGES_MESSAGE}
          onConfirm={handleStartNewOptimizationClick}
          onCancel={() => setConfirmModalOpen(false)}
        />

        <ConfirmationDialog
          open={confirmDuplicateTemplateExistModalOpen}
          title='Confirm Template Name'
          contentText={DUPLICATE_TEMPLATE_NAME_MESSAGE}
          onConfirm={handleConfirmTemplateName}
          onCancel={() => setConfirmDuplicateTemplateExistModalOpen(false)}
          confirmButtonText='Yes'
          cancelButtonText='No'
        />
      </Paper>
    </PortfolioOptimizationLayout>
  )
}
